import { Grid, GridCell, GridRow } from '@angular/aria/grid';
import {
  afterRenderEffect,
  booleanAttribute,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { MibWeekday } from '../../../foundations';
import { addDays, isSameDay, monthWeeks, startOfDay, startOfWeek } from '../../../internal/dates.utils';
import { MibIconComponent } from '../../icons';
import { addMonths } from '../picker.utils';

interface MibDatepickerCell {
  day: Date;
  key: string;
  inMonth: boolean;
  disabled: boolean;
  today: boolean;
  /** Picked: the single date, or either end of a range. */
  selected: boolean;
  /** Strictly inside a range, or inside the one being drawn. */
  inRange: boolean;
  rangeStart: boolean;
  rangeEnd: boolean;
  /** The hovered end of a range still being drawn. */
  preview: boolean;
}

/** A month of the year view, or a year of the years view. */
interface MibDatepickerChoice {
  value: Date;
  key: string;
  label: string;
  disabled: boolean;
  current: boolean;
  selected: boolean;
}

/** As Material: the days of a month, the months of a year, or a page of years. */
export type MibDatepickerView = 'month' | 'year' | 'years';

const YEARS_SHOWN = 24;

const keyOf = (d: Date): string => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
const inRows = <T>(cells: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(cells.length / size) }, (_, i) => cells.slice(i * size, (i + 1) * size));

/** The month a date picker or a date range picker opens under its field. */
@Component({
  selector: 'mib-datepicker-panel',
  imports: [Grid, GridRow, GridCell, MibIconComponent],
  templateUrl: './date-picker-panel.component.html',
  styleUrl: './date-picker-panel.component.css',
  host: { class: 'block w-72' },
})
export class MibDatepickerPanelComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** The day picked, in single mode. */
  readonly selected = input<Date | null>(null);
  /** Draws a range: `start` and `end` are its ends, and a day hovered after `start` previews it. */
  readonly range = input(false, { transform: booleanAttribute });
  /** The first day of the range, in range mode. */
  readonly start = input<Date | null>(null);
  /** The last day of the range, in range mode. */
  readonly end = input<Date | null>(null);
  /** The end is kept but not yet confirmed: it stays drawn while a day hovered after `start` previews a new one. */
  readonly pendingEnd = input(false, { transform: booleanAttribute });
  /** The first day that can be picked. */
  readonly min = input<Date | null>(null);
  /** The last day that can be picked. */
  readonly max = input<Date | null>(null);
  /** A BCP 47 tag for the names of the months and the days; the browser's by default. */
  readonly locale = input<string | undefined>(undefined);
  /** The first day of the week, as `Date.getDay()` numbers it: 0 is Sunday. */
  readonly weekStartsOn = input<MibWeekday>(1);
  /** Accessible name of the previous-month button. */
  readonly previousLabel = input('Previous month');
  /** Accessible name of the next-month button. */
  readonly nextLabel = input('Next month');
  /** Any day of the month shown. */
  readonly month = model<Date>(startOfDay(new Date()));

  /** Emits the day picked with a click, Enter or Space. */
  readonly picked = output<Date>();

  protected readonly view = signal<MibDatepickerView>('month');

  /** The day under the pointer or the keyboard, for the range preview. */
  protected readonly hovered = signal<Date | null>(null);
  protected readonly drawnEnd = computed(() => {
    const start = this.start();
    const hovered = this.hovered();
    return this.pendingEnd() && start && hovered && hovered > start ? null : this.end();
  });
  private active: Date | null = null;
  /** The cell the keyboard is on, by its `data-mib-date`; focused once the grid has drawn it. */
  private readonly focusTarget = signal<{ key: string } | null>(null);

  private readonly formats = computed(() => {
    const locale = this.locale();
    return {
      title: new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }),
      weekday: new Intl.DateTimeFormat(locale, { weekday: 'narrow' }),
      weekdayLong: new Intl.DateTimeFormat(locale, { weekday: 'long' }),
      day: new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
    };
  });

  protected readonly title = computed(() => {
    const month = this.month();
    if (this.view() === 'month') return this.formats().title.format(month);
    if (this.view() === 'year') return String(month.getFullYear());
    const first = this.firstYear();
    return `${first} – ${first + YEARS_SHOWN - 1}`;
  });

  private readonly firstYear = computed(() => Math.floor(this.month().getFullYear() / YEARS_SHOWN) * YEARS_SHOWN);

  protected readonly choices = computed<MibDatepickerChoice[][]>(() => {
    const month = this.month();
    const picked = this.range() ? this.start() : this.selected();
    const today = new Date();
    if (this.view() === 'year') {
      const format = new Intl.DateTimeFormat(this.locale(), { month: 'short' });
      const months = Array.from({ length: 12 }, (_, m) => {
        const value = new Date(month.getFullYear(), m, 1);
        const same = (d: Date | null): boolean => !!d && d.getFullYear() === value.getFullYear() && d.getMonth() === m;
        return {
          value,
          key: `m${m}`,
          label: format.format(value),
          disabled: !this.reaches(value, new Date(month.getFullYear(), m + 1, 0)),
          current: same(today),
          selected: same(picked),
        };
      });
      return inRows(months, 4);
    }
    const years = Array.from({ length: YEARS_SHOWN }, (_, i) => {
      const year = this.firstYear() + i;
      return {
        value: new Date(year, month.getMonth(), 1),
        key: `y${year}`,
        label: String(year),
        disabled: !this.reaches(new Date(year, 0, 1), new Date(year, 11, 31)),
        current: today.getFullYear() === year,
        selected: picked?.getFullYear() === year,
      };
    });
    return inRows(years, 4);
  });

  protected readonly weekdays = computed(() => {
    const first = startOfWeek(this.month(), this.weekStartsOn());
    const f = this.formats();
    return Array.from({ length: 7 }, (_, i) => {
      const day = addDays(first, i);
      return { short: f.weekday.format(day), long: f.weekdayLong.format(day) };
    });
  });

  protected readonly weeks = computed<MibDatepickerCell[][]>(() => {
    const month = this.month();
    const today = startOfDay(new Date());
    const [from, to] = this.span();
    return monthWeeks(month, this.weekStartsOn(), []).map((week) =>
      week.map((day) => {
        const inMonth = day.getMonth() === month.getMonth();
        const rangeStart = !!from && isSameDay(day, from);
        const rangeEnd = !!to && isSameDay(day, to);
        const single = !this.range() && !!this.selected() && isSameDay(day, this.selected()!);
        const kept = this.pendingEnd() && !!this.end() && isSameDay(day, this.end()!);
        return {
          day,
          key: keyOf(day),
          inMonth,
          disabled: !inMonth || !this.allowed(day),
          today: isSameDay(day, today),
          selected: single || rangeStart || kept || (rangeEnd && !!this.drawnEnd()),
          inRange: !!from && !!to && day > from && day < to,
          rangeStart,
          rangeEnd,
          preview: rangeEnd && !this.drawnEnd(),
        };
      }),
    );
  });

  /** The range drawn: the one held, or the one previewed from `start` to the hovered day. */
  private readonly span = computed<[Date | null, Date | null]>(() => {
    if (!this.range()) return [null, null];
    const start = this.start();
    const end = this.drawnEnd();
    const hovered = this.hovered();
    if (start && !end && hovered && hovered > start) return [start, hovered];
    return [start, end];
  });

  /** The first and last day of what the panel shows. */
  private readonly shown = computed<[Date, Date]>(() => {
    const month = this.month();
    if (this.view() === 'month')
      return [
        new Date(month.getFullYear(), month.getMonth(), 1),
        new Date(month.getFullYear(), month.getMonth() + 1, 0),
      ];
    if (this.view() === 'year') return [new Date(month.getFullYear(), 0, 1), new Date(month.getFullYear(), 11, 31)];
    return [new Date(this.firstYear(), 0, 1), new Date(this.firstYear() + YEARS_SHOWN - 1, 11, 31)];
  });

  protected readonly canGoBack = computed(() => {
    const min = this.min();
    return !min || this.shown()[0] > startOfDay(min);
  });

  protected readonly canGoForward = computed(() => {
    const max = this.max();
    return !max || this.shown()[1] < startOfDay(max);
  });

  constructor() {
    afterRenderEffect(() => {
      const target = this.focusTarget();
      if (!target) return;
      this.host.nativeElement.querySelector<HTMLElement>(`[data-mib-date="${target.key}"]`)?.focus();
    });
  }

  /** Puts the keyboard on the day picked, else today, else the first day that can be picked. */
  focus(): void {
    const month = this.month();
    const wanted = (this.range() ? this.start() : this.selected()) ?? startOfDay(new Date());
    const inShown = wanted.getMonth() === month.getMonth() && wanted.getFullYear() === month.getFullYear();
    const first = this.weeks()
      .flat()
      .find((c) => !c.disabled)?.day;
    this.moveTo(inShown && this.allowed(wanted) ? wanted : (first ?? wanted));
  }

  protected label(cell: MibDatepickerCell): string {
    return this.formats().day.format(cell.day);
  }

  protected previous(): void {
    this.month.set(addMonths(this.month(), -this.step()));
  }

  protected next(): void {
    this.month.set(addMonths(this.month(), this.step()));
  }

  private step(): number {
    return this.view() === 'month' ? 1 : this.view() === 'year' ? 12 : 12 * YEARS_SHOWN;
  }

  protected switchView(): void {
    this.view.set(this.view() === 'month' ? 'years' : 'month');
    if (this.view() === 'month') this.focus();
    else this.focusChoice();
  }

  protected choose(choice: MibDatepickerChoice): void {
    if (choice.disabled) return;
    this.month.set(choice.value);
    if (this.view() === 'years') {
      this.view.set('year');
      this.focusChoice();
    } else {
      this.view.set('month');
      this.focus();
    }
  }

  protected onChoiceKeydown(event: KeyboardEvent, choice: MibDatepickerChoice): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    this.choose(choice);
  }

  private focusChoice(): void {
    const cells = this.choices().flat();
    const usable = cells.filter((c) => !c.disabled);
    const target = usable.find((c) => c.selected) ?? usable.find((c) => c.current) ?? usable[0];
    if (target) this.focusTarget.set({ key: target.key });
  }

  protected pick(cell: MibDatepickerCell): void {
    if (cell.disabled) return;
    this.picked.emit(cell.day);
  }

  /* The grid's own arrows stop at the edge of the month; these cross it, as a date picker does. */
  protected onKeydown(event: KeyboardEvent, cell: MibDatepickerCell): void {
    const day = this.active ?? cell.day;
    const moves: Record<string, () => Date> = {
      ArrowLeft: () => addDays(day, -1),
      ArrowRight: () => addDays(day, 1),
      ArrowUp: () => addDays(day, -7),
      ArrowDown: () => addDays(day, 7),
      Home: () => startOfWeek(day, this.weekStartsOn()),
      End: () => addDays(startOfWeek(day, this.weekStartsOn()), 6),
      PageUp: () => addMonths(day, event.shiftKey ? -12 : -1),
      PageDown: () => addMonths(day, event.shiftKey ? 12 : 1),
    };
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.pick(cell);
      return;
    }
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    event.stopPropagation();
    this.moveTo(move());
  }

  /* Focused at once when the day is drawn, so a key pressed before the next render starts from it. */
  private moveTo(day: Date): void {
    this.active = day;
    this.hovered.set(day);
    const month = this.month();
    if (day.getMonth() === month.getMonth() && day.getFullYear() === month.getFullYear()) {
      this.host.nativeElement.querySelector<HTMLElement>(`[data-mib-date="${keyOf(day)}"]`)?.focus();
      return;
    }
    this.month.set(day);
    this.focusTarget.set({ key: keyOf(day) });
  }

  protected onFocus(cell: MibDatepickerCell): void {
    this.active = cell.day;
  }

  private reaches(from: Date, to: Date): boolean {
    const min = this.min();
    const max = this.max();
    return (!min || to >= startOfDay(min)) && (!max || from <= startOfDay(max));
  }

  private allowed(day: Date): boolean {
    const min = this.min();
    const max = this.max();
    return (!min || day >= startOfDay(min)) && (!max || day <= startOfDay(max));
  }
}
